import type { AttributeType, RelationType } from "@judo/model-api";

/**
 * Build a JUDO mask string from attributes and relations.
 *
 * The mask format wraps comma-separated entries in curly braces.
 * Plain attributes are listed by name, while relations include their
 * target attributes in nested braces.
 *
 * @example
 * // Attributes only
 * buildMaskString({ attributes: [{ name: "born" }, { name: "name" }] })
 * // => "{born,name}"
 *
 * @example
 * // Attributes + relations
 * buildMaskString({
 *   attributes: [{ name: "born" }, { name: "name" }],
 *   relations: [{ name: "messages", target: { attributes: [{ name: "message" }] } }],
 * })
 * // => "{born,name,messages{message}}"
 *
 * @example
 * // Empty produces "{}"
 * buildMaskString({ attributes: [] })
 * // => "{}"
 */
export function buildMaskString(params: { attributes?: AttributeType[]; relations?: RelationType[] }): string {
	const { attributes = [], relations = [] } = params;

	const parts: string[] = [];

	// Add attribute names
	for (const attr of attributes) {
		if (attr.name) {
			parts.push(attr.name);
		}
	}

	// Add relation masks with nested target attributes
	for (const rel of relations) {
		if (rel.name) {
			const targetAttrs = (rel.target?.attributes ?? []).map((a) => a.name).filter(Boolean);
			if (targetAttrs.length > 0) {
				parts.push(`${rel.name}{${targetAttrs.join(",")}}`);
			} else {
				parts.push(`${rel.name}{}`);
			}
		}
	}

	return `{${parts.join(",")}}`;
}
